import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { useToastStore, type Toast } from '../../stores/useToastStore';

const AUTO_DISMISS_MS = 6000;

/** Global toast outlet. Mount once near the app root; toasts are pushed
 *  imperatively via `useToastStore` (see `toastApiError`). Bottom-right,
 *  stacked, auto-dismissing. */
export function Toaster(): JSX.Element | null {
  const toasts = useToastStore((s) => s.toasts);
  if (toasts.length === 0) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        bottom: 18,
        right: 18,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxWidth: 360,
        pointerEvents: 'none',
      }}
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>,
    document.body,
  );
}

function ToastCard({ toast }: { toast: Toast }): JSX.Element {
  const dismiss = useToastStore((s) => s.dismiss);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    // Next frame → trigger the enter transition.
    const raf = requestAnimationFrame(() => setShown(true));
    const timer = setTimeout(() => dismiss(toast.id), AUTO_DISMISS_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [toast.id, dismiss]);

  const accent = toast.variant === 'error' ? 'var(--color-oxblood)' : 'var(--color-ochre)';

  return (
    <div
      role="alert"
      style={{
        pointerEvents: 'auto',
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '12px 12px 12px 14px',
        background: 'hsl(var(--color-bone))',
        border: '1px solid hsl(var(--color-ink))',
        borderLeft: `2px solid hsl(${accent})`,
        borderRadius: 2,
        boxShadow: '0 6px 20px rgba(60,40,20,0.10), 0 1px 2px rgba(60,40,20,0.06)',
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : 'translateY(2px)',
        transition: 'opacity 150ms ease-out, transform 150ms ease-out',
      }}
    >
      <AlertTriangle
        size={16}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ color: `hsl(${accent})`, flexShrink: 0, marginTop: 1 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.3,
            color: 'hsl(var(--color-ink))',
            margin: 0,
          }}
        >
          {toast.title}
        </p>
        {toast.description && (
          <p
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'hsl(var(--color-muted))',
              margin: '4px 0 0',
            }}
          >
            {toast.description}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={() => dismiss(toast.id)}
        aria-label="Dismiss"
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          padding: 0,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: 'hsl(var(--color-muted))',
        }}
      >
        <X size={14} strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" />
      </button>
    </div>
  );
}
